import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { api } from "../../services/apiService";
import { logger } from "../../services/logService";
import { getQueryParam } from "../../utils/common";
import TableSkeleton from "../skeletons/TableSkeleton";

const DataTable = ({
  endpoint,
  data: externalData, // rename to avoid confusion
  columns,
  defaultSort = { field: "id", direction: "asc" },
  emptyStateContent,
  emptyMessage, // alias for emptyStateContent (used by ClientManagementPage, etc.)
  filters = {},
  onRowClick,
  refreshTrigger = 0,
  loading: externalLoading,
  onSort: externalSort,
  sortBy: controlledSortBy,
  sortOrder: controlledSortOrder,
}) => {
  const effectiveEmptyContent = emptyStateContent || emptyMessage;
  const [internalData, setInternalData] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [isSidebarClosed, setIsSidebarClosed] = useState(true);
  const hasUserSortedRef = useRef(false);

  // Sync sortConfig with parent-controlled sortBy/sortOrder when provided (e.g. ClientManagementPage)
  // Supports null for reset state (3-state cycle: asc → desc → reset)
  useEffect(() => {
    if (controlledSortBy === undefined && controlledSortOrder === undefined)
      return;
    setSortConfig((current) => {
      const newField = controlledSortBy ?? null;
      const newDirection = controlledSortOrder ?? null;
      if (current.field === newField && current.direction === newDirection)
        return current;
      return { field: newField, direction: newDirection };
    });
  }, [controlledSortBy, controlledSortOrder]);
  // Fetch data with current sort configuration
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  const storageListener = ({ detail }) => {
    setIsSidebarClosed(detail.collapsed);
  };
  useEffect(() => {
    setIsSidebarClosed(
      document.getElementsByClassName("sidebar")?.[0].clientWidth < 100,
    );
    window.addEventListener("sidebarEvent", storageListener);
    return () => {
      window.removeEventListener("sidebarEvent", storageListener);
    };
  }, []);
  useEffect(() => {
    if (!externalSort || !sortConfig) return;
    // When parent controls sort (externalData + sortBy/sortOrder), skip the initial mount call.
    // Parent already has correct state and will fetch; calling externalSort here would trigger
    // handleSort → setState → new loadClients ref → useEffect re-runs → duplicate API call.
    if (
      !hasUserSortedRef.current &&
      controlledSortBy !== undefined &&
      controlledSortOrder !== undefined
    ) {
      return;
    }
    externalSort({ field: sortConfig.field, direction: sortConfig.direction });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig?.field, sortConfig?.direction]);

  const fetchData = useCallback(async () => {
    // Only fetch if we have an endpoint and no external data
    if (!endpoint || externalData) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // When reset (direction null), use defaultSort for API
      const effectiveSort =
        sortConfig.direction === null || sortConfig.direction === undefined
          ? defaultSort
          : sortConfig;
      const sort = {
        sort_by: effectiveSort.field,
        sort_order: effectiveSort.direction.toUpperCase(),
      };
      const extra = {
        client_id: getQueryParam("client_id"),
      };

      const response = await api.getSortedData(endpoint, {
        sort,
        filters,
        page,
        limit,
        extra,
      });

      if (response.success) {
        const data =
          response?.data?.test_cases ||
          response?.test_cases ||
          response?.data?.clients ||
          [];
        setInternalData(data);
        const total =
          response?.data?.pagination?.total || response.pagination?.total || 0;
        setTotalPages(Math.ceil(total / limit));
      } else {
        console.error("DataTable: API returned success: false", response);
        setError(response.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("DataTable: Error fetching data:", err);
      console.error("DataTable: Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
      });

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch table data";
      setError(errorMessage);
      logger.error(err, errorMessage);
    } finally {
      setLoading(false);
    }
  }, [endpoint, sortConfig, filters, page, limit, externalData]);

  // Only fetch if we have an endpoint and no external data
  useEffect(() => {
    if (endpoint && !externalData) {
      fetchData();
    }
  }, [fetchData, endpoint, externalData, refreshTrigger]);

  // 3-state sort cycle: 1st click = asc ↑, 2nd click = desc ↓, 3rd click = reset ↕
  const handleSort = (field) => {
    hasUserSortedRef.current = true;
    setSortConfig((currentSort) => {
      if (currentSort.field !== field) {
        return { field, direction: "asc" };
      }
      if (currentSort.direction === "asc") return { field, direction: "desc" };
      if (currentSort.direction === "desc")
        return { field: null, direction: null };
      return { field, direction: "asc" };
    });
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field || sortConfig.direction === null) {
      return <ArrowUpDown className="w-3.5 h-3.5 inline" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5 inline" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 inline" />
    );
  };

  // Use external data if provided, otherwise use internal data
  const rawData = externalData || internalData;
  // Client-side sort when we have external data, no onSort, and active sort (for tables like UsersTab)
  const displayData = useMemo(() => {
    if (!rawData?.length || !sortConfig?.field || sortConfig.direction === null)
      return rawData;
    if (externalSort) return rawData; // Parent controls sort when onSort provided
    const sorted = [...rawData];
    const field = sortConfig.field;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    sorted.sort((a, b) => {
      let aVal = a[field] ?? "";
      let bVal = b[field] ?? "";
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [rawData, sortConfig?.field, sortConfig?.direction, externalSort]);
  const isLoading = externalLoading || loading;

  if (isLoading) {
    return (
      <div className="min-h-[200px] w-full py-2">
        <TableSkeleton rows={6} columns={Math.min(columns.length, 6)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-error">
        {error}
      </div>
    );
  }

  // If empty and emptyStateContent/emptyMessage is provided, show table headers with empty state in tbody
  if (!displayData.length && effectiveEmptyContent) {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border-primary">
          <thead className="bg-bg-secondary">
            <tr>
              {columns.map(({ field, key, header, sortable = false }) => {
                const fieldName = field || key;
                return (
                  <th
                    key={fieldName}
                    className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider"
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td colSpan={columns.length} className="px-6 py-8">
                {effectiveEmptyContent}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (!displayData.length) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-text-tertiary">
        {effectiveEmptyContent || "No data available"}
      </div>
    );
  }

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <div
      className="overflow-hidden"
      style={{
        maxWidth: isSidebarClosed
          ? "calc(100vw - 72px)"
          : "calc(100vw - 228px)",
      }}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-bg-gray">
            <tr>
              {columns.map(({ field, key, header, sortable = false }) => {
                const fieldName = field || key;
                return (
                  <th
                    key={fieldName}
                    onClick={() => sortable && handleSort(fieldName)}
                    className={`px-6 py-3 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider ${
                      sortable ? "cursor-pointer hover:divide-gray-200" : ""
                    }`}
                  >
                    <div
                      className="flex items-center space-x-1 "
                      style={{ color: "var(--color-gray-60)" }}
                    >
                      {typeof header === "string" ? (
                        <span>{header}</span>
                      ) : (
                        header
                      )}
                      {sortable && (
                        <span className="text-text-tertiary">
                          {getSortIcon(fieldName)}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-bg-lightgray divide-y ">
            {displayData.map((row, index) => {
              return (
                <tr
                  key={row.id || row.test_case_id || index}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={
                    onRowClick
                      ? "cursor-pointer hover:bg-bg-selectedgray"
                      : "hover:bg-bg-selectedgray"
                  }
                >
                  {columns.map(({ field, key, render }) => {
                    const fieldName = field || key;
                    const cellValue = row[fieldName];
                    let display = render ? render(cellValue, row) : cellValue;
                    // Prevent "Objects are not valid as React child" - API may return nested objects (e.g. created_by)
                    if (
                      display !== null &&
                      display !== undefined &&
                      typeof display === "object" &&
                      !React.isValidElement(display)
                    ) {
                      display = display?.full_name ?? display?.name ?? "-";
                    }
                    return (
                      <td
                        key={fieldName}
                        className="px-6 py-3 whitespace-nowrap text-sm text-gray-900"
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-4 pb-4">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 rounded bg-bg-tertiary disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-text-secondary">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="px-3 py-1 rounded bg-bg-tertiary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
